// =========================================================================
// HD derivation + address encoding for the signing service (blueprint §3, §4).
//
// One master seed (S-HOT) → deterministic per-party deposit addresses on TRON
// (coin 195) and the EVM mirror (coin 60, same index = same party). The BFF
// holds only an account-level xpub and can VERIFY any address the signer hands
// back (public-only derivation) without ever holding the seed.
//
// Deps are deliberately minimal and audited (Paul Miller's noble/scure): this
// is the whole cryptographic surface of the signer, so it stays small, pinned,
// and vendored — never pulled from CI (blueprint §3, supply-chain discipline).
// =========================================================================

import { HDKey } from '@scure/bip32';
import { mnemonicToSeedSync, generateMnemonic, validateMnemonic } from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english';
import { secp256k1 } from '@noble/curves/secp256k1';
import { keccak_256 } from '@noble/hashes/sha3';
import { bytesToHex, hexToBytes } from '@noble/hashes/utils';
// Address encoding lives in its own module so the co-sign page can use it
// without pulling in derivation. Re-exported here for existing callers.
import { decodeTronAddress, encodeTronAddress, TRON_ADDRESS_PREFIX } from './address.mjs';

export { decodeTronAddress, encodeTronAddress, TRON_ADDRESS_PREFIX };

export const COIN = { TRON: 195, EVM: 60 };
const TRON_PREFIX = TRON_ADDRESS_PREFIX;

// ---- paths ---------------------------------------------------------------

// Full BIP-44 path to a leaf key. account/change default to 0/0; index varies
// per party for L0 deposit addresses. Non-leaf layers (L1=account 1, etc.)
// pass their own account and index 0 — see blueprint §3 derivation map.
export function path(coin, { account = 0, change = 0, index = 0 } = {}) {
  return `m/44'/${coin}'/${account}'/${change}/${index}`;
}

// The account-level node the BFF is given (everything up to and including the
// last hardened level). From this xpub the BFF can derive change/index publicly
// but cannot sign. e.g. m/44'/195'/0'
export function accountPath(coin, account = 0) {
  return `m/44'/${coin}'/${account}'`;
}

// ---- seed / root ---------------------------------------------------------

export function newMnemonic(strength = 256) {
  return generateMnemonic(wordlist, strength);
}

// passphrase = the BIP-39 25th word (blueprint §3: stored separately from the
// metal plates, so a stolen plate alone can't reconstruct the tree).
//
// A phrase with a misspelt word or a bad checksum is refused. mnemonicToSeedSync
// hashes whatever it is given, so a transcription error during a recovery
// would otherwise derive a different wallet, silently (review finding A3).
export function seedFromMnemonic(mnemonic, passphrase = '') {
  if (typeof mnemonic !== 'string' || !validateMnemonic(mnemonic, wordlist)) {
    throw new Error('not a valid BIP-39 English mnemonic — a misspelt word or a bad checksum; nothing was derived');
  }
  return mnemonicToSeedSync(mnemonic, passphrase);
}

export function rootFromSeed(seed) {
  return HDKey.fromMasterSeed(seed);
}

// ---- address encoding ----------------------------------------------------

// Uncompressed public key (65 bytes, 0x04 || X || Y) from either a private key
// or a compressed public key.
function uncompressedPubkey({ privateKey, publicKey }) {
  if (privateKey) return secp256k1.getPublicKey(privateKey, false);
  // public-only path (xpub): decompress the 33-byte key to 65 bytes.
  return secp256k1.ProjectivePoint.fromHex(publicKey).toRawBytes(false);
}

// TRON address: keccak256(pubkey[1:])[-20:], prefix 0x41, base58check.
export function tronAddressFromPubkey(node) {
  const pub = uncompressedPubkey(node).slice(1); // drop 0x04
  const hash = keccak_256(pub);
  const body = new Uint8Array(21);
  body[0] = TRON_PREFIX;
  body.set(hash.slice(-20), 1);
  return encodeTronAddress(body);
}

// EVM address with EIP-55 checksum casing.
export function evmAddressFromPubkey(node) {
  const pub = uncompressedPubkey(node).slice(1);
  const hash = keccak_256(pub);
  const addr = bytesToHex(hash.slice(-20)); // lowercase, no 0x
  const check = bytesToHex(keccak_256(new TextEncoder().encode(addr)));
  let out = '0x';
  for (let i = 0; i < addr.length; i++) {
    out += parseInt(check[i], 16) >= 8 ? addr[i].toUpperCase() : addr[i];
  }
  return out;
}


// ---- derivation ----------------------------------------------------------

// Signer-side: full derivation with the private key present (can sign).
export function deriveTron(seed, index, opts = {}) {
  const p = path(COIN.TRON, { ...opts, index });
  const node = rootFromSeed(seed).derive(p);
  return {
    path: p,
    address: tronAddressFromPubkey(node),
    publicKeyHex: bytesToHex(node.publicKey),
    privateKeyHex: bytesToHex(node.privateKey),
  };
}

export function deriveEvm(seed, index, opts = {}) {
  const p = path(COIN.EVM, { ...opts, index });
  const node = rootFromSeed(seed).derive(p);
  return {
    path: p,
    address: evmAddressFromPubkey(node),
    publicKeyHex: bytesToHex(node.publicKey),
    privateKeyHex: bytesToHex(node.privateKey),
  };
}

// BFF-side: the account xpub, safe to hold off the signer (no private key).
export function accountXpub(seed, coin, account = 0) {
  return rootFromSeed(seed).derive(accountPath(coin, account)).publicExtendedKey;
}

// ---- Ledger-rooted deposit addresses (pilot topology) --------------------
//
// For the hardware-only pilot the HD root is a LEDGER's seed, not a server
// seed, so no private key for deposit addresses exists on any machine.
//
// The catch: TronLink enumerates Ledger accounts by HARDENED account index
// (m/44'/195'/i'/0/0), not by address index under one account. So each party
// gets its own hardened account. Consequence worth understanding:
//
//   Hardened children CANNOT be derived from a parent xpub. With this layout
//   the BFF cannot expand one xpub into N addresses — each party's account
//   xpub must be exported from the device individually. In practice that means
//   pre-exporting a batch of account xpubs during the key ceremony (cheap: one
//   device session) rather than one export per new party.
//
// The flat layout (deriveTron below, m/44'/195'/0'/0/i) keeps single-xpub
// expansion and is the right shape once a server-side signer exists.

export function ledgerAccountPath(partyIndex) {
  return path(COIN.TRON, { account: partyIndex, change: 0, index: 0 });
}

// Signer/ceremony-side: derive a party's deposit address under its own
// hardened account, matching what TronLink will show for that Ledger.
export function deriveTronLedgerStyle(seed, partyIndex) {
  return deriveTron(seed, 0, { account: partyIndex });
}

// BFF-side: verify a Ledger-style deposit address from that party's own
// exported account xpub. One xpub per party, by construction.
export function tronAddressFromPartyXpub(partyXpub) {
  return tronAddressFromXpub(partyXpub, 0, { change: 0 });
}

// The account-level public key the BFF holds — and only that. HDKey reads a
// private extended key just as happily, which would put a signing key where
// none belongs; and a key at another depth derives addresses that are not the
// account's.
function accountPublicNode(xpub) {
  if (typeof xpub === 'string' && xpub.startsWith('xprv')) {
    throw new Error('that is a private extended key (xprv) — this side holds public keys only');
  }
  // A serialised extended key is 82 bytes, always 111 characters of base58;
  // checked before decoding, which is superlinear in the length.
  if (typeof xpub !== 'string' || xpub.length !== 111 || !xpub.startsWith('xpub')) {
    throw new Error('expected an account xpub — 111 characters, starting "xpub"');
  }
  const node = HDKey.fromExtendedKey(xpub);
  if (node.privateKey) throw new Error('that is a private extended key — this side holds public keys only');
  if (node.depth !== 3) {
    throw new Error(`an account xpub is at depth 3 (m/44'/coin'/account'); this one is at depth ${node.depth}`);
  }
  return node;
}

// BFF-side: derive an address from the account xpub alone (public-only). This
// is how the BFF verifies the signer's derive_address response without the seed.
export function tronAddressFromXpub(xpub, index, { change = 0 } = {}) {
  const node = accountPublicNode(xpub).deriveChild(change).deriveChild(index);
  return tronAddressFromPubkey(node);
}

export function evmAddressFromXpub(xpub, index, { change = 0 } = {}) {
  const node = accountPublicNode(xpub).deriveChild(change).deriveChild(index);
  return evmAddressFromPubkey(node);
}

// ---- an account key as a device exports it (17 Oct 2026) -----------------
//
// A Ledger's TRON app hands out an account's public key and chain code
// (APDU E0 02 with P2 = 1; cosign/src/ledger.ts exportAccountKey) — the two
// parts of an extended public key, without the serialisation around them.
// This puts them back into a standard xpub (BIP32's own version bytes,
// 0488B21E), so tronAddressFromXpub() expands the account to its deposit
// addresses unchanged, and the string in admin.wallet_keys is the same one a
// wallet app would export for that account.
//
// The child number is the account's own step: account 0 is 0' = 0x80000000.
// The parent fingerprint is hash160 of the parent's (m/44'/195') compressed
// key, first four bytes — part of the serialisation, not of any derivation;
// zero when the caller does not have it, and the key still derives the same
// addresses. The test pins the string built from the dev mnemonic's own
// account node equal to accountXpub() for that account, byte for byte.

export const HARDENED_OFFSET = 0x80000000;

/** Bytes from a Uint8Array or a hex string; refuses anything else by name. */
function bytesOf(value, what, length) {
  let out;
  if (value instanceof Uint8Array) out = value;
  else if (typeof value === 'string' && /^([0-9a-fA-F]{2})+$/.test(value)) out = hexToBytes(value.toLowerCase());
  else throw new Error(`${what} must be bytes or hex`);
  if (!length.includes(out.length)) throw new Error(`${what} is ${out.length} bytes; expected ${length.join(' or ')}`);
  return out;
}

/** A 32-bit field as a number: given as one, or as eight hex characters. */
function u32Of(value, what) {
  const n = typeof value === 'string' && /^[0-9a-fA-F]{8}$/.test(value) ? parseInt(value, 16) : value;
  if (!Number.isInteger(n) || n < 0 || n > 0xffffffff) throw new Error(`${what} must be a whole number below 2^32, or eight hex characters`);
  return n;
}

/**
 * xpubFromAccountKey({ publicKey, chainCode, depth = 3, childNumber, parentFingerprint = 0 })
 * → the account's xpub string.
 *
 *   publicKey          65 bytes (04 ‖ X ‖ Y, as the TRON app answers) or 33 compressed; bytes or hex
 *   chainCode          32 bytes; bytes or hex
 *   childNumber        the account step, hardened: account n is n + HARDENED_OFFSET
 *   parentFingerprint  a number or eight hex characters; 0 when unknown
 *
 * A key that is not a point on the curve is refused by the library; the
 * message never carries the key.
 */
export function xpubFromAccountKey({ publicKey, chainCode, depth = 3, childNumber, parentFingerprint = 0 }) {
  const pub = bytesOf(publicKey, 'publicKey', [65, 33]);
  if (pub.length === 65 && pub[0] !== 0x04) throw new Error('a 65-byte public key starts with 04');
  if (pub.length === 33 && pub[0] !== 0x02 && pub[0] !== 0x03) throw new Error('a 33-byte public key starts with 02 or 03');
  const code = bytesOf(chainCode, 'chainCode', [32]);
  if (!Number.isInteger(depth) || depth < 0 || depth > 255) throw new Error('depth must be a whole number from 0 to 255');
  const index = u32Of(childNumber, 'childNumber');
  const fingerprint = u32Of(parentFingerprint, 'parentFingerprint');
  let node;
  try {
    node = new HDKey({ publicKey: pub, chainCode: code, depth, index, parentFingerprint: fingerprint });
  } catch {
    throw new Error('the public key is not a point on the curve — nothing was built');
  }
  return node.publicExtendedKey;
}

/**
 * What an account xpub says about itself: which hardened account it is, its
 * depth and its parent's fingerprint. Refuses a private key, a key at any
 * depth but the account's, and an account step that is not hardened — the
 * row in admin.wallet_keys is read through this, never trusted by prefix.
 */
export function accountOfXpub(xpub) {
  const node = accountPublicNode(xpub);
  if (node.index < HARDENED_OFFSET) {
    throw new Error("an account step is hardened (m/44'/coin'/account'); this key's last step is not");
  }
  return { account: node.index - HARDENED_OFFSET, depth: node.depth, parentFingerprint: node.parentFingerprint };
}

/**
 * The key MATERIAL an account xpub carries — the compressed public key and the
 * chain code, as hex — which is what makes two xpubs the same key whatever
 * their metadata (depth, child number, parent fingerprint) says: a row is
 * matched by this, never by the serialised string (Screens' Astra review,
 * 23 Oct 2026: a retired key re-registered under another fingerprint).
 */
export function materialOfXpub(xpub) {
  const node = accountPublicNode(xpub);
  return { publicKey: bytesToHex(node.publicKey), chainCode: bytesToHex(node.chainCode) };
}

export { bytesToHex, hexToBytes };
